// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import Search from "@adl/XDL/Icons/Search";
import { color, xScreen } from "@adl/XDL/Utilities";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import { IconWrapper } from "@insite/mobius/Icon";
import Link, { LinkProps } from "@insite/mobius/Link";
import Select, { SelectPresentationProps } from "@insite/mobius/Select";
import TextField, { TextFieldProps } from "@insite/mobius/TextField";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC, useEffect, useState } from "react";
import { css } from "styled-components";

type Props = {
    alphabet: LetterDetails[];
    prefix?: string;
    extendedStyles?: AlphabetNavigationStyles;
    onLetterClick?: (letter: string) => void;
    // XNG-Changes
    industries?: string[];
    handleSearchFilterBrands?: (e: React.ChangeEvent<HTMLInputElement>) => void;
    handleIndustryFilterBrands?: (letter: string) => void;
    searchFilterBrands?: string;
    selectedLetter?: string;
    industrySelected?: string;
};

export interface LetterDetails {
    letter: string;
    linkable: boolean;
}

export interface AlphabetNavigationStyles {
    container?: InjectableCss;
    navigationItem?: InjectableCss;
    navigationLink?: LinkProps;
    navigationLinkText?: TypographyPresentationProps;
    navigationTextEmpty?: TypographyPresentationProps;
    // XNG-Changes
    navTitle?: TypographyPresentationProps;
    searchTitle?: TypographyPresentationProps;
    brandsLettersWrapper?: InjectableCss;
    alphabetSection?: InjectableCss;
    filterSection?: InjectableCss;
    filterInput?: TextFieldProps;
    filterInputWrapper?: InjectableCss;
    industrySelect?: SelectPresentationProps;
}

export const alphabetNavigationStyles: AlphabetNavigationStyles = {
    container: {
        css: css`
            display: flex;
            flex-direction: row;
            justify-content: space-between;
            background-color: ${color("common.ui9")};
            padding: 20px 70px 26px 20px;
            margin-bottom: 52px;
            ${xScreen("lte", "lg")} {
                padding: 20px 20px 26px;
            }
            ${xScreen("lte", "md")} {
                flex-direction: column;
                padding: 20px 16px 18px;
            }
        `,
    },
    navTitle: {
        size: 16,
        weight: "bold",
        css: css`
            line-height: 22px;
            letter-spacing: 0;
            margin-bottom: 18px;

            ${xScreen("lte", "sm")} {
                margin-bottom: 10px;
            }
        `,
    },
    searchTitle: {
        size: 16,
        weight: "bold",
        css: css`
            line-height: 22px;
            margin-bottom: 10px;
        `,
    },
    brandsLettersWrapper: {
        css: css`
            display: flex;
            align-items: flex-start;
            text-transform: capitalize;
            flex-wrap: wrap;
            flex-direction: column;
            justify-content: space-between;
        `,
    },
    navigationItem: {
        css: css`
            display: inline-flex;
            width: 28px;
            height: 20px;
            &.alpha-navigation-item-last-item {
                margin-right: 60px;
            }

            ${({ selectedLetter, enabled }: any) =>
                selectedLetter &&
                enabled &&
                css`
                    background-color: ${color("common.ui1")};
                    border-radius: 2px;
                    &&& ${TypographyStyle} {
                        color: ${color("common.white")};
                    }
                    &:hover {
                        background-color: ${color("common.ui1")};
                    }
                `}
        `,
    },
    navigationLink: {
        color: "secondary",
        css: css`
            &:focus {
                outline: none;
            }
        `,
        typographyProps: {
            transform: "capitalize",
            variant: "headerTertiary",
            forwardAs: "span",
            size: 15,
            weight: "bold",
            css: css`
                text-align: center;
                display: block;
                letter-spacing: 0;
                line-height: 26px;
                width: 28px;
            `,
        },
    },
    navigationTextEmpty: {
        transform: "capitalize",
        variant: "headerTertiary",
        forwardAs: "span",
        color: "text.disabled",
        size: 15,
        weight: "bold",
        css: css`
            text-align: center;
            display: block;
            letter-spacing: 0;
            line-height: 26px;
            width: 24px;
            opacity: 0.66;
            cursor: not-allowed;
            margin-top: -3px;
        `,
    },
    alphabetSection: {
        css: css`
            display: flex;
            flex-wrap: wrap;
            ${xScreen("lte", "md")} {
                margin-bottom: 10px;
            }
        `,
    },
    filterSection: {
        css: css`
            display: flex;
            align-items: flex-start;
            flex-direction: column;
        `,
    },
    filterInput: {
        mergeCss: true,
        cssOverrides: {
            formField: css`
                width: 262px;
                ${xScreen("lte", "sm")} {
                    width: 100%;
                }
            `,
            formInputWrapper: css`
                && {
                    > input {
                        height: 42px;
                    }
                    ${IconWrapper} {
                        top: 4px;
                    }
                }
            `,
        },
        iconProps: { src: Search, color: "secondary" },
    },
    filterInputWrapper: {
        css: css`
            ${xScreen("lte", "md")} {
                width: 100%;
            }
        `,
    },
    industrySelect: {
        css: css`
            width: 220px;
            margin-right: 30px;
            margin-bottom: 18px;
        `,
    },
};

const StyledSpan = getStyledWrapper("span");

const AlphabetNavigation: FC<Props> = (props: Props) => {
    const [styles] = useState(() => mergeToNew(alphabetNavigationStyles, props.extendedStyles));
    const [alphabet] = useState(() => [{ letter: "all", count: 0, linkable: true }, ...props.alphabet]);
    const [selectedLetter, setSelectedLetter] = useState(props.selectedLetter);

    // XNG-Changes: To get selectedLetter change when a user click on clean Filter
    useEffect(() => {
        setSelectedLetter(props.selectedLetter);
    }, [props.selectedLetter]);

    const handleClickOfLetter = (letter: string) => {
        props.onLetterClick?.(letter);
    };

    const handleIndustryFilterBrands = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const industry = event.currentTarget.value;
        props.handleIndustryFilterBrands?.(industry);
    };

    // XNG-Changes
    const createHandleClickOfLetter = (letter: string) => () => {
        setSelectedLetter(letter);
        return handleClickOfLetter(letter);
    };

    // XNG-Changes: we add `All` as a first element
    if (alphabet.length <= 1) {
        return null;
    }

    return (
        <StyledWrapper {...styles.container} data-test-selector="alphabetNavigation">
            <StyledWrapper {...styles.brandsLettersWrapper}>
                <Typography {...styles.navTitle}>{translate("Alphabetical Navigation")}</Typography>
                <StyledWrapper {...styles.alphabetSection}>
                    {alphabet.map((alphabetItem, index) => (
                        <StyledSpan
                            {...styles.navigationItem}
                            {...{
                                selectedLetter: selectedLetter === alphabetItem.letter && alphabetItem.linkable,
                                enabled: !props.searchFilterBrands && !props.industrySelected && alphabetItem.linkable,
                            }}
                            className={index === alphabet.length - 1 ? "alpha-navigation-item-last-item" : ""}
                            key={alphabetItem.letter}
                            data-test-selector="alphabetNavigationItemLetter"
                        >
                            {!props.searchFilterBrands && !props.industrySelected && alphabetItem.linkable ? (
                                <Link
                                    {...styles.navigationLink}
                                    onClick={createHandleClickOfLetter(alphabetItem.letter)}
                                >
                                    {alphabetItem.letter}
                                </Link>
                            ) : (
                                <Typography {...styles.navigationTextEmpty}>{alphabetItem.letter}</Typography>
                            )}
                        </StyledSpan>
                    ))}
                </StyledWrapper>
            </StyledWrapper>
            <StyledWrapper {...styles.filterSection}>
                <Typography {...styles.searchTitle}>{translate("Search By Name")}</Typography>
                {props.industries && props.industries.length > 0 && (
                    <StyledWrapper {...styles.industrySelect}>
                        <Select
                            required
                            placeholder={translate("Filter by Industry")}
                            onChange={handleIndustryFilterBrands}
                        >
                            <option value="">{translate("Filter by Industry")}</option>
                            {...props.industries.map(industry => (
                                <option key={industry} value={industry}>
                                    {industry}
                                </option>
                            ))}
                        </Select>
                    </StyledWrapper>
                )}
                <StyledWrapper {...styles.filterInputWrapper}>
                    <TextField
                        name="filterBrands"
                        value={props.searchFilterBrands}
                        placeholder={translate("Search By Name")}
                        onChange={props.handleSearchFilterBrands}
                        {...styles.filterInput}
                    />
                </StyledWrapper>
            </StyledWrapper>
        </StyledWrapper>
    );
};

export default AlphabetNavigation;
